import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Intercom } from '@supy-io/ngx-intercom';
import { DateTime } from 'luxon';
import { forkJoin } from 'rxjs';
import { DistrictSimpleDto } from 'src/app/common/dtos/district.dto';
import { UserPermission } from 'src/app/common/state/user/permission/user-permission';
import { UserLiteDTO } from 'src/app/common/state/user/user.dto';
import { User } from 'src/app/common/state/user/user.model';
import { UserService } from 'src/app/common/state/user/user.service';
import {
  oneDecimalValidator,
  pdSessionTypes,
  positiveValidator,
  valueLimitValidator,
} from 'src/app/common/utilities/pd-helpers';
import { checkIfE2L } from 'src/app/common/utilities/role-helpers';
import {
  getTimezoneLinkBackFromTimezone,
  TimezoneLinkBack,
} from 'src/app/common/utilities/time-helpers';
import { CompetencySelectComponent } from 'src/app/private/shared/components/competency/competency-select/competency-select.component';
import { DatepickerOutput } from 'src/app/private/shared/components/datepicker/datepicker.component';
import { DistrictSelectComponent } from 'src/app/private/shared/components/district-select/district-select.component';
import { FileListComponent } from 'src/app/private/shared/components/file-management/file-list/file-list.component';
import {
  FileUploadComponent,
  UploadStyles,
} from 'src/app/private/shared/components/file-management/file-upload/file-upload.component';
import { WysiwygEditorComponent } from 'src/app/private/shared/components/wysiwyg-editor/wysiwyg-editor.component';
import { CompetencyDTO } from 'src/app/private/shared/dtos/competencies.dto';
import { FileDTO } from 'src/app/private/shared/dtos/file.dto';
import {
  CreatePdSessionPayload,
  PdCategoryOption,
  PdCreditAwardingMethod,
  PdSession,
  PdSettings,
  UpdatePdSessionPayload,
} from 'src/app/private/shared/dtos/pd.dto';
import { UploadType } from 'src/app/private/shared/enums/upload-type.enum';
import { getIcon } from 'src/app/private/shared/helpers/icon.utilities';
import { CompetenciesSelectService } from 'src/app/private/shared/services/competencies/competencies-select.service';
import { LmsCourseListService } from 'src/app/private/shared/services/lms-courses/lms-course-list.service';
import { LMSCourseService } from 'src/app/private/shared/services/lms-courses/lms-course.service';
import { ProformsFormListService } from 'src/app/private/shared/services/proforms/proforms-formlist.service';
import { CURRENT_SCHOOL_YEAR } from 'src/app/private/shared/services/school-year/school-year.utilities';
import { FEATURE_FLAGS } from 'src/app/private/shared/services/unleash/unleash.helpers';
import { UnleashService } from 'src/app/private/shared/services/unleash/unleash.service';
import { NewProFormPayload } from 'src/app/private/shared/types/payloads/proforms.payloads';
import {
  GetAllLmsCoursesAPIResponse,
  LmsCourseAPIResponse,
} from 'src/app/private/shared/types/responses/lms-course.responses';
import {
  GetAllProFormsAPIResponse,
  ItemProFormsAPIResponse,
  ProFormsAPIResponse,
  ProFormStatus,
} from 'src/app/private/shared/types/responses/proforms.responses';
import { PdService } from '../../../shared/services/pd/pd.service';
import { CopyPdSessionModalComponent } from '../copy-pd-session-modal/copy-pd-session-modal.component';

const initialPayload: CreatePdSessionPayload = {
  title: '',
  description: '',
  type: 'Microlearning',
  is_shared: 0,
  image: '',
  start_datetime: 0,
  end_datetime: 0,
  location: '',
  is_virtual: 1,
  virtual_link: null,
  max_registrations: 0,
  survey_proform_form_id: 0,
  notes: '',
  credits: [],
  facilitators: [],
  competencies: [],
  categories: [],
  sharing: [],
  status: 'draft',
};

@Component({
  selector: 'app-create-microlearning-course',
  templateUrl: './create-microlearning-course.component.html',
  styleUrl: './create-microlearning-course.component.scss',
  standalone: false,
})
export class CreateMicrolearningCoursePageComponent
  implements OnInit, OnDestroy
{
  @ViewChild('copyPdSessionModal')
  copyPdSessionModal: CopyPdSessionModalComponent;

  @ViewChild('uploadComponent') uploadComponent: FileUploadComponent;

  @ViewChild('fileListComponent') fileListComponent: FileListComponent;

  @ViewChild('courseContentEditor') courseContentEditor: WysiwygEditorComponent;

  @ViewChild('selectCompetency') selectCompetency: CompetencySelectComponent;

  @ViewChild('shareWithDistrictSelect')
  shareWithDistrictSelect: DistrictSelectComponent;

  @ViewChild('excludeFromDistrictSelect')
  excludeFromDistrictSelect: DistrictSelectComponent;

  user: User;

  isE2L = false;

  canCreate = false;

  usersDistrictHasSessions = true;

  // 1. content
  // 2. quiz
  // 3. eventDetails
  currentScreen: 'content' | 'quiz' | 'eventDetails' = 'content';

  sessionType: 'liveEvent' | 'microlearning';

  form: FormGroup;

  contentFormSubmitted = false;

  eventDetailsFormSubmitted = false;

  hasCourseContent = false;

  imagePath = '';

  uploadStyle = UploadStyles.SIMPLE;

  uploadType = UploadType.USER_UPLOAD;

  pdSessionTypes = pdSessionTypes;

  creditData: { creditType: string; creditAmount: number }[] = [];

  selectedCreditTypes: string[] = [];

  categoryData: { category: string; categoryOptions: PdCategoryOption }[] = [];

  selectedOptionsByCategory: { [category: string]: string[] } = {};

  settings: PdSettings;

  districtIds: number[] = [];

  currentCompetencies: CompetencyDTO[] = [];

  surveyFormList: ProFormsAPIResponse[] = [];

  firstDayOfSchoolYear = DateTime.fromISO(CURRENT_SCHOOL_YEAR.start_date)
    .startOf('day')
    .toSeconds();

  lastDayOfSchoolYear = Math.floor(
    DateTime.fromISO(CURRENT_SCHOOL_YEAR.end_date).endOf('day').toSeconds()
  );

  defaultTimezone: TimezoneLinkBack = TimezoneLinkBack.Central;

  facilitators: UserLiteDTO[] = [];

  payload: CreatePdSessionPayload = { ...initialPayload };

  errorMessage = '';

  lmsErrorMessage = '';

  contentSubmitLoading = false;

  eventDetailsSubmitLoading = false;

  proFormsEnabled = false;

  sessionToCopy: PdSession | null = null;

  sessionToContinue: PdSession | null = null;

  copiedQuiz: ProFormsAPIResponse;

  lmsCourses: LmsCourseAPIResponse[];

  lmsCourseFileList: LmsCourseAPIResponse[] = [];

  lmsCourseIsUploading = false;

  quizId: number;

  sessionId: number;

  defaultDistrictsSharedWith: number[] = [];

  defaultDistrictsExcludedFrom: number[] = [];

  microlearningEnabled = false;

  facilitatorInvalid = false;

  PdCreditAwardingMethod = PdCreditAwardingMethod;

  constructor(
    private store: Store,
    private userService: UserService,
    private router: Router,
    private formBuilder: FormBuilder,
    private pdService: PdService,
    private competenciesSelectService: CompetenciesSelectService,
    private proformsFormListService: ProformsFormListService,
    private featureFlagService: UnleashService,
    private intercom: Intercom,
    private lmsCourseListService: LmsCourseListService,
    private lmsCourseService: LMSCourseService,
    private cdr: ChangeDetectorRef
  ) {
    this.user = this.store.selectSnapshot((state) => state.user.user) as User;
    if (this.user) {
      this.isE2L = checkIfE2L(this.user);
      if (this.user.district) {
        const districtTimezone = getTimezoneLinkBackFromTimezone(
          this.user.district.timezone
        );
        if (districtTimezone) {
          this.defaultTimezone = districtTimezone;
        }
        if (!this.isE2L) {
          this.districtIds = [this.user.district.id];
          this.payload.district_id = this.user.district?.id;
        }
      }
    }

    this.proFormsEnabled = this.featureFlagService.isEnabled(
      FEATURE_FLAGS.proForms
    );

    this.microlearningEnabled = this.featureFlagService.isEnabled(
      FEATURE_FLAGS.microlearning
    );

    if (!this.microlearningEnabled) {
      this.router.navigate(['/permission-denied']);
    }

    const nav = this.router.getCurrentNavigation();
    if (nav?.extras.state) {
      if (nav.extras.state['sessionToCopy']) {
        this.sessionToCopy = nav.extras.state['sessionToCopy'] as PdSession;
        this.copySession(this.sessionToCopy);
      }
      if (nav.extras.state['sessionToContinue']) {
        this.sessionToContinue = nav.extras.state[
          'sessionToContinue'
        ] as PdSession;
        this.copySession(this.sessionToContinue);
        this.sessionId = this.sessionToContinue.id;
        if (this.sessionToContinue.quiz_proform_form_id) {
          this.quizId = this.sessionToContinue.quiz_proform_form_id;
        }
      }
    }
  }

  ngOnInit() {
    if (!this.sessionToCopy && !this.sessionToContinue) {
      this.resetPayload();
    }
    this.getSettings();
    this.getSurveys();
    this.getLmsCourses();
    this.checkUserPermissions();
  }

  resetPayload() {
    this.payload = {
      ...initialPayload,
      start_datetime: this.firstDayOfSchoolYear,
      end_datetime: this.lastDayOfSchoolYear,
      registration_start_datetime: this.firstDayOfSchoolYear,
      registration_end_datetime: this.lastDayOfSchoolYear,
    };
  }

  resetForm() {
    this.resetPayload();
    this.imagePath = '';
    this.contentFormSubmitted = false;
    this.creditData = [];
    this.categoryData = [];
    this.currentCompetencies = [];
    this.initializeForm();
  }

  checkUserPermissions() {
    this.userService.fetchUserPermissions().subscribe((res) => {
      if (res) {
        this.canCreate = res.items.some(
          (item: { name: string }) =>
            item.name === UserPermission.CREATE_PD_SESSION
        );
        if (!this.canCreate) {
          this.router.navigate(['/permission-denied']);
        } else {
          if (!this.sessionToCopy && !this.sessionToContinue) {
            this.initializeForm();
          }
          this.facilitators = [this.user];
          this.payload.facilitators = [this.user.id];
        }
      }
    });
  }

  get f() {
    return this.form.controls;
  }

  getSettings() {
    this.pdService
      .getAdminSettings(this.user?.district?.id)
      .subscribe((res) => {
        if (res.item) {
          this.settings = res.item;
        }
      });
  }

  getSurveys() {
    this.proformsFormListService
      .getForms(this.user?.district?.id, 50, 1, 'survey', 'published')
      .subscribe((resp: GetAllProFormsAPIResponse) => {
        if (resp.items) {
          this.surveyFormList = resp.items;
        }
      });
  }

  initializeForm() {
    this.form = this.formBuilder.group({
      title: ['', Validators.required],
      shortDescription: ['', Validators.required],
      credits: this.formBuilder.array(
        this.creditData.map((credit) => this.formBuilder.group(credit))
      ),
      categories: this.formBuilder.array(
        this.categoryData.map((category) => this.formBuilder.group(category))
      ),
      survey: [null],
      lmsCourse: [null],
      isShared: new FormControl(false),
      creditAward: [PdCreditAwardingMethod.SESSION_COMPLETED],
    });
  }

  copySession(session: PdSession) {
    this.form = this.formBuilder.group({
      title: [
        `${session.title}${this.sessionToCopy ? ' (Copy)' : ''}`,
        Validators.required,
      ],
      shortDescription: [session.description, Validators.required],
      credits: session.credits
        ? this.formBuilder.array(
            session.credits.map((credit) =>
              this.formBuilder.group({
                creditType: [credit.credit_type, Validators.required],
                creditAmount: [
                  credit.credit_amount,
                  [
                    Validators.required,
                    positiveValidator(),
                    valueLimitValidator(),
                  ],
                ],
              })
            )
          )
        : this.formBuilder.array([
            this.creditData.map((credit) => this.formBuilder.group(credit)),
          ]),
      categories: session.categories
        ? this.formBuilder.array(
            session.categories.map((category) =>
              this.formBuilder.group({
                category: [category.category, Validators.required],
                categoryOptions: [category.value, Validators.required],
              })
            )
          )
        : this.formBuilder.array(
            this.categoryData.map((category) =>
              this.formBuilder.group(category)
            )
          ),
      survey: [
        session.survey_proform_form_id ? session.survey_proform_form_id : null,
      ],
      lmsCourse: [session.lms_course_id || null],
      isShared: [session.is_shared === 1],
      creditAward: [
        session.credit_awarding_method ||
          PdCreditAwardingMethod.SESSION_COMPLETED,
      ],
    });

    this.updateSelectedCreditTypes();
    this.updateSelectedOptions();

    if (session.image) {
      this.payload.image = session.image;
      this.imagePath = getIcon(session.image);
    }

    this.payload.notes = session.notes;
    if (session.competencies) {
      session.competencies.forEach((competency) => {
        this.competenciesSelectService
          .getCompetency(competency.egrowe_standard_id)
          .subscribe((comp) => {
            if (comp) {
              this.currentCompetencies.push(comp);
            }
          });
      });
    }

    if (session.sharing) {
      session.sharing.forEach((share) => {
        if (share.is_shared === 1) {
          this.defaultDistrictsSharedWith.push(share.district_id);
        } else {
          this.defaultDistrictsExcludedFrom.push(share.district_id);
        }
      });
    }

    if (this.sessionToContinue) {
      this.payload.registration_start_datetime =
        session.registration_start_datetime;
      this.payload.registration_end_datetime =
        session.registration_end_datetime;
      this.payload.start_datetime = session.registration_start_datetime;
      this.payload.end_datetime = session.registration_end_datetime;
    }
  }

  // LMS
  getLmsCourses(): void {
    if (this.user?.district) {
      this.lmsCourseListService
        .getCourses(this.user.district.id)
        .subscribe((resp: GetAllLmsCoursesAPIResponse) => {
          if (resp.items) {
            this.lmsCourses = resp.items;
          }
        });
    }
  }

  uploadLmsCourse(file: FileDTO) {
    this.lmsCourseIsUploading = true;
    const courseFile = file.location;
    if (courseFile) {
      this.lmsCourseService.createCourse(courseFile).subscribe((res) => {
        if (res.error) {
          this.lmsErrorMessage = res.error;
          this.lmsCourseIsUploading = false;
        }
        if (res.item) {
          this.lmsCourseFileList = [res.item];
          this.lmsCourseIsUploading = false;
          this.hasCourseContent = true;
          this.lmsErrorMessage = '';
        }
      });
    }
  }

  validateCourseNotes(content: string) {
    if (content && content.length > 0) {
      this.hasCourseContent = true;
    } else {
      this.hasCourseContent = false;
    }
    this.cdr.detectChanges();
  }

  deleteLmsCourseFile(file: LmsCourseAPIResponse) {
    this.lmsCourseFileList = this.lmsCourseFileList.filter(
      (lmsCourseFile) => lmsCourseFile.id !== file.id
    );
  }

  // CREDITS
  get credits(): FormArray {
    return this.form.get('credits') as FormArray;
  }

  addCredit() {
    this.credits.push(
      this.formBuilder.group({
        creditType: [null, Validators.required],
        creditAmount: [
          null,
          [
            Validators.required,
            positiveValidator(),
            valueLimitValidator(),
            oneDecimalValidator(),
          ],
        ],
      })
    );
    this.watchCreditTypeChanges();
  }

  deleteCredit(index: number) {
    this.credits.removeAt(index);
    this.updateSelectedCreditTypes();
  }

  watchCreditTypeChanges() {
    this.credits.valueChanges.subscribe(() => this.updateSelectedCreditTypes());
  }

  updateSelectedCreditTypes() {
    this.selectedCreditTypes = [];
    this.credits.controls.forEach((group) => {
      const creditType = group.get('creditType')?.value;
      if (creditType && creditType !== null) {
        this.selectedCreditTypes.push(creditType);
      }
    });
  }

  isCreditTypeSelected(creditTitle: string, currentIndex: number): boolean {
    const isSelected = this.selectedCreditTypes.includes(creditTitle);
    const currentControl = this.credits.at(currentIndex).get('creditType');
    return isSelected && currentControl?.value !== creditTitle;
  }

  // CATEGORIES
  get categories(): FormArray {
    return this.form.get('categories') as FormArray;
  }

  addCategory() {
    const newCategoryGroup = this.formBuilder.group({
      category: [null, Validators.required],
      categoryOptions: [null, Validators.required],
    });

    // Watch for changes in the first dropdown (category)
    newCategoryGroup.get('category')?.valueChanges.subscribe(() => {
      newCategoryGroup.get('categoryOptions')?.reset(); // Reset the second dropdown when the category changes
      this.updateSelectedOptions(); // Update selected options globally
    });

    // Watch for changes in the second dropdown (categoryOptions)
    newCategoryGroup.get('categoryOptions')?.valueChanges.subscribe(() => {
      this.updateSelectedOptions(); // Recompute selected options for all categories
    });

    this.categories.push(newCategoryGroup);
  }

  updateSelectedOptions() {
    this.selectedOptionsByCategory = {}; // Reset the selected options tracking

    this.categories.controls.forEach((control) => {
      const category = control.get('category')?.value;
      const option = control.get('categoryOptions')?.value;

      if (category && option) {
        if (!this.selectedOptionsByCategory[category]) {
          this.selectedOptionsByCategory[category] = [];
        }

        if (!this.selectedOptionsByCategory[category].includes(option)) {
          this.selectedOptionsByCategory[category].push(option);
        }
      }
    });
  }

  getFilteredOptions(categoryTitle: string): PdCategoryOption[] {
    // Return options relevant to the selected category and not already selected for it
    const category = this.settings.categories.find(
      (cat) => cat.title === categoryTitle
    );

    if (!category) return []; // No options if the category is invalid or not found

    return category.options.filter(
      (option) =>
        !this.selectedOptionsByCategory[categoryTitle]?.includes(
          option.option_value
        ) // Exclude already-selected options
    );
  }

  deleteCategory(index: number) {
    this.categories.removeAt(index);
    this.updateSelectedOptions(); // Update selected options after deletion
  }

  addCompetencies(competencies: CompetencyDTO[] | null) {
    if (competencies) {
      if (competencies !== undefined || competencies !== null) {
        this.currentCompetencies = competencies;
      }
    } else {
      this.currentCompetencies = [];
    }
  }

  // IMAGE UPLOAD
  updateImage(file: FileDTO) {
    this.payload.image = file.location;
    this.imagePath = getIcon(file.location);
  }

  deleteImage() {
    this.payload.image = '';
    this.imagePath = '';
  }

  // NAVIGATION
  handleBackNavigation() {
    this.resetPayload();
    this.pdService.getSessions().subscribe((resp) => {
      const hasMicrolearningSessions = resp.items.some(
        (s: PdSession) => s.type === 'Microlearning'
      );
      const screenToNavigateTo = hasMicrolearningSessions
        ? 'newOrExisting'
        : 'chooseEventType';

      this.router.navigate(['/training/create-session'], {
        state: {
          currentScreen: screenToNavigateTo,
          sessionType: 'microlearning',
        },
      });
    });
  }

  navigateToQuiz() {
    this.contentFormSubmitted = true;
    // Check that user has either selected a course from library, uploaded their own, or filled out WYSIWYG editor
    this.hasCourseContent =
      this.form.get('lmsCourse')?.value ||
      this.lmsCourseFileList.length > 0 ||
      this.courseContentEditor.editorContent?.length > 0;
    const contentFormIsValid =
      this.form.get('title')?.valid &&
      this.form.get('shortDescription')?.valid &&
      this.hasCourseContent;

    if (contentFormIsValid) {
      this.contentSubmitLoading = true;
      this.payload.title = this.form.get('title')?.value;
      this.payload.description = this.form.get('shortDescription')?.value;
      this.payload.notes = this.courseContentEditor.editorContent;
      this.payload.lms_course_id = this.lmsCourseFileList.length
        ? this.lmsCourseFileList[0].id
        : this.form.get('lmsCourse')?.value || 0;

      if (!this.sessionId) {
        this.pdService.createSession(this.payload).subscribe({
          error: (err) => {
            this.errorMessage = `There was an issue with your request: ${err.error.message}`;
            this.contentSubmitLoading = false;
          },
          next: (res) => {
            const metadata = {
              pd_session_id: res.item.id,
              user_id: this.user.id,
              district_id: res.item.district_id,
            };
            this.intercom.trackEvent('PD Create Event', metadata);

            this.sessionId = res.item.id;
            this.contentSubmitLoading = false;
            // If copying a session, copy the quiz if it exists
            if (this.sessionToCopy?.quiz_proform_form_id) {
              this.copyQuiz(
                this.sessionToCopy.quiz_proform_form_id,
                res.item.title
              );
            } else {
              // If not copying, create a new quiz
              this.createQuiz(res.item.title, res.item.id);
            }
            this.currentScreen = 'quiz';
          },
        });
      } else {
        this.pdService.updateSession(this.sessionId, this.payload).subscribe({
          error: (err) => {
            this.errorMessage = `There was an issue with your request: ${err.error.message}`;
            this.contentSubmitLoading = false;
          },
          next: () => {
            this.contentSubmitLoading = false;
            this.currentScreen = 'quiz';
          },
        });
      }
    }
  }

  createQuiz(title: string, sessionId: number) {
    if (this.user) {
      const payload: NewProFormPayload = {
        title: `${title}`,
        outro: 'Your response has been submitted. Thank you for your input!',
        type: 'quiz',
        status: ProFormStatus.PUBLISHED,
      };
      this.proformsFormListService
        .createForm(payload)
        .subscribe((resp: ItemProFormsAPIResponse) => {
          if (resp.item.id) {
            this.pdService
              .updateSession(sessionId, { quiz_proform_form_id: resp.item.id })
              .subscribe((response) => {
                if (response) {
                  this.quizId = resp.item.id;
                }
              });
          }
        });
    }
  }

  copyQuiz(quizId: number, title: string) {
    this.proformsFormListService
      .copyForm(quizId, { title })
      .subscribe((res) => {
        if (res) {
          this.copiedQuiz = res.item;
          this.pdService
            .updateSession(this.sessionId, {
              quiz_proform_form_id: res.item.id,
            })
            .subscribe();
        }
      });
  }

  // DATE & TIME
  updateRegistrationFromDate(output: DatepickerOutput[]) {
    // setTimeout needed to prevent NG0100: ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      const fromDate = output[0].time / 1000;
      this.payload.registration_start_datetime = fromDate;
      this.payload.start_datetime = fromDate;
    }, 0);
  }

  updateRegistrationToDate(output: DatepickerOutput[]) {
    const toDate = Math.floor(
      DateTime.fromMillis(output[0].time).endOf('day').toSeconds()
    );
    this.payload.registration_end_datetime = toDate;
    this.payload.end_datetime = toDate;
  }

  // FACILITATORS
  alreadyAdded(user: UserLiteDTO) {
    return this.facilitators.find((facilitator) => facilitator.id === user.id);
  }

  addFacilitator(facilitator: UserLiteDTO | null) {
    if (facilitator) {
      if (!this.alreadyAdded(facilitator)) {
        this.facilitators.push(facilitator);
        this.facilitatorInvalid = false;
      }
    }
  }

  removeFacilitator(facilitator: UserLiteDTO) {
    this.facilitators = this.facilitators.filter(
      (fac) => fac.id !== facilitator.id
    );
  }

  updateSharing(
    selectedDistricts: DistrictSimpleDto[],
    isShared: number
  ): void {
    // Track updated district IDs for efficient processing
    const updatedDistricts = selectedDistricts.map((district) => district.id);

    // Handle the opposite dropdown
    const oppositeDropdown =
      isShared === 1
        ? this.excludeFromDistrictSelect
        : this.shareWithDistrictSelect;

    // Update the opposite dropdown's internalSelectedDistrictList
    oppositeDropdown.internalSelectedDistrictList =
      oppositeDropdown.internalSelectedDistrictList.filter(
        (district) => !updatedDistricts.includes(district.id)
      );

    // Update the payload sharing
    this.payload.sharing = this.payload.sharing.filter((item) => {
      if (item.is_shared !== isShared) {
        // Remove districts that are newly selected in this dropdown
        const index = updatedDistricts.indexOf(item.district_id);
        if (index !== -1) {
          updatedDistricts.splice(index, 1);
          return false; // Remove conflicting entry
        }
        return true; // Retain entries with different is_shared values
      }

      // Retain selected districts
      const index = updatedDistricts.indexOf(item.district_id);
      if (index !== -1) {
        updatedDistricts.splice(index, 1); // Remove from list to add
        return true;
      }

      // Remove deselected districts
      return false;
    });

    // Add any new districts to the payload
    updatedDistricts.forEach((districtId) => {
      this.payload.sharing.push({
        district_id: districtId,
        is_shared: isShared,
      });
    });
  }

  publishCourse() {
    this.eventDetailsFormSubmitted = true;

    if (this.facilitators.length === 0) {
      this.facilitatorInvalid = true;
      return;
    }

    // 1. Validate the form
    const eventDetailsFormIsValid =
      this.credits.controls.every(
        (group) =>
          group.get('creditType')?.valid && group.get('creditAmount')?.valid
      ) &&
      this.categories.controls.every(
        (group) =>
          group.get('category')?.valid && group.get('categoryOptions')?.valid
      );

    if (!eventDetailsFormIsValid) {
      return;
    }

    this.eventDetailsSubmitLoading = true;

    // 2. Prepare all requests
    const creditRequests = this.credits.controls
      .filter((group) => {
        const creditType = group.get('creditType')?.value;
        const creditAmount = group.get('creditAmount')?.value;
        return creditType && creditAmount;
      })
      .map((group) =>
        this.pdService.createCredit(this.sessionId, {
          credit_type: group.get('creditType')?.value,
          credit_amount: group.get('creditAmount')?.value,
        })
      );

    const categoryRequests = this.categories.controls
      .filter((group) => {
        const category = group.get('category')?.value;
        const categoryOptions = group.get('categoryOptions')?.value;
        return category && categoryOptions;
      })
      .map((group) =>
        this.pdService.createCategory(this.sessionId, {
          category: group.get('category')?.value,
          value: group.get('categoryOptions')?.value,
        })
      );

    const competencyRequests = this.currentCompetencies.map((competency) =>
      this.pdService.updateCompetency(this.sessionId, {
        egrowe_standard_id: competency.id,
      })
    );

    const sharingRequests = this.payload.sharing.map((sharing) =>
      sharing.is_shared === 1
        ? this.pdService.shareWithDistrict(this.sessionId, sharing.district_id)
        : this.pdService.excludeFromDistrict(
            this.sessionId,
            sharing.district_id
          )
    );

    const originalFacilitatorId = this.user.id;
    const newFacilitatorIds = this.facilitators.map((f) => f.id);

    // Add requests: anyone in new list except the current user
    const addFacilitatorRequests = newFacilitatorIds
      .filter((id) => id !== originalFacilitatorId)
      .map((id) =>
        this.pdService.addFacilitator(this.sessionId, { user_id: id })
      );

    // Remove request: if current user is not in the new list, remove them
    const removeFacilitatorRequests = !newFacilitatorIds.includes(
      originalFacilitatorId
    )
      ? [
          this.pdService.deleteFacilitator(
            this.sessionId,
            originalFacilitatorId
          ),
        ]
      : [];

    // Combine for forkJoin
    const facilitatorRequests = [
      ...addFacilitatorRequests,
      ...removeFacilitatorRequests,
    ];

    // 3. Prepare updateSession request
    this.payload.survey_proform_form_id = this.form.get('survey')?.value;
    this.payload.is_shared = this.form.get('isShared')?.value ? 1 : 0;
    this.payload.credit_awarding_method = this.form.get('creditAward')?.value;
    this.payload.status = 'published';

    const updatePayload: UpdatePdSessionPayload = {
      is_shared: this.payload.is_shared,
      credit_awarding_method: this.payload.credit_awarding_method,
      ...(this.payload.survey_proform_form_id
        ? { survey_proform_form_id: this.payload.survey_proform_form_id }
        : {}),
      status: this.payload.status,
    };

    const updateSessionRequest = this.pdService.updateSession(
      this.sessionId,
      updatePayload
    );

    // 4. Run all requests in parallel and handle errors
    forkJoin([
      ...creditRequests,
      ...categoryRequests,
      ...competencyRequests,
      ...sharingRequests,
      ...facilitatorRequests,
      updateSessionRequest,
    ]).subscribe({
      next: () => {
        this.eventDetailsSubmitLoading = false;
        // Check for errors in results (if any API returns a custom error object)
        // If all succeed, navigate and track event
        this.router.navigate([
          `training/session/microlearning/${this.sessionId}`,
        ]);
        const metadata = {
          pd_session_id: this.sessionId,
          user_id: this.user.id,
          district_id: this.user.district?.id,
        };
        this.intercom.trackEvent('PD Create Event', metadata);
      },
      error: (err) => {
        this.eventDetailsSubmitLoading = false;
        this.errorMessage = `There was an issue publishing your course: ${
          err?.error?.message || err
        }`;
      },
    });
  }

  ngOnDestroy() {
    this.resetPayload();
  }
}
